/**
 * useTouchFeedback — haptic + sound + glow coordinate handler
 * Convenience hook for <Link> elements and non-button tappables
 * where AnimatedButton can't be used.
 */

import { useCallback } from 'react';
import { useHaptic } from './useHaptic';
import { useSoundEffects, type SoundType } from './useSoundEffects';

interface TouchFeedbackOptions {
  sound?: SoundType;
  hapticPattern?: 'tap' | 'success';
}

export function useTouchFeedback(options?: TouchFeedbackOptions) {
  const haptic = useHaptic();
  const { play } = useSoundEffects();

  const sound = options?.sound ?? 'tap';
  const hapticPattern = options?.hapticPattern ?? 'tap';

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      haptic[hapticPattern]();
      play(sound);

      // Set glow coordinates for .touch-glow CSS
      const el = e.currentTarget;
      if (el) {
        const rect = el.getBoundingClientRect();
        el.style.setProperty(
          '--touch-x',
          `${((e.clientX - rect.left) / rect.width) * 100}%`,
        );
        el.style.setProperty(
          '--touch-y',
          `${((e.clientY - rect.top) / rect.height) * 100}%`,
        );
      }
    },
    [haptic, hapticPattern, play, sound],
  );

  return { onPointerDown };
}
